"use client";
import { BulletList } from "@tiptap/extension-bullet-list";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  ImagePlus,
  Italic,
  List,
  Strikethrough,
  UnderlineIcon,
  X,
} from "lucide-react";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const MAX_IMAGES = 5;

const EMOJIS = [
  "😊",
  "😂",
  "❤️",
  "👍",
  "🎉",
  "🙏",
  "😍",
  "😭",
  "🤔",
  "😅",
  "🥰",
  "👏",
  "😎",
  "🤗",
  "💪",
  "🌸",
  "✨",
  "💯",
  "🎊",
  "😇",
];

interface Props {
  onSubmit: (html: string, images: File[]) => void;
  onCancel?: () => void;
  placeholder?: string;
  isPending?: boolean;
  autoFocus?: boolean;
  initialContent?: string;
}

const TopicReplyEditor = ({
  onSubmit,
  onCancel,
  placeholder = "Düşüncelerini yaz...",
  isPending = false,
  autoFocus = false,
  initialContent,
}: Props) => {
  const [active, setActive] = useState(autoFocus || !!initialContent);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!initialContent);
  const [images, setImages] = useState<File[]>([]);
  const emojiRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imagePreviews = useMemo(
    () => images.map((file) => URL.createObjectURL(file)),
    [images],
  );

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imagePreviews]);

  const editor = useEditor({
    content: initialContent,
    editorProps: {
      attributes: {
        class:
          "outline-none min-h-[72px] text-sm text-neutral-700 leading-relaxed px-3 py-2",
      },
    },
    extensions: [
      StarterKit.configure({ bulletList: false }),
      BulletList,
      Underline,
    ],
    immediatelyRender: false,
    onFocus: () => setActive(true),
    onUpdate: ({ editor }) => setIsEmpty(editor.isEmpty),
  });

  const handleSubmit = () => {
    if (!editor || (isEmpty && images.length === 0)) {
      return;
    }
    onSubmit(editor.getHTML(), images);
    editor.commands.clearContent();
    setImages([]);
    if (!autoFocus) {
      setActive(false);
    }
  };

  const handleCancel = () => {
    editor?.commands.clearContent();
    setImages([]);
    setActive(false);
    onCancel?.();
  };

  const insertEmoji = (emoji: string) => {
    editor?.chain().focus().insertContent(emoji).run();
    setShowEmoji(false);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      return [...prev, ...files.slice(0, remaining)];
    });
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };
  useEffect(() => {
    if (active && editor) {
      editor.commands.focus();
    }
  }, [active, editor]);

  if (!active) {
    return (
      <button
        className="w-full text-left rounded-lg border border-neutral-200 px-3 py-2.5 text-sm text-neutral-400 hover:border-brand-blue-300 transition-colors"
        onClick={() => setActive(true)}
        type="button"
      >
        {placeholder}
      </button>
    );
  }

  const isSubmitDisabled = isPending || (isEmpty && images.length === 0);

  return (
    <div className="rounded-lg border border-neutral-200 focus-within:border-brand-blue-300 transition-colors">
      <EditorContent editor={editor} />

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pb-2">
          {images.map((file, i) => (
            <div
              className="relative size-16 shrink-0"
              key={`${file.name}-${file.lastModified}`}
            >
              <Image
                alt={file.name}
                className="rounded-md object-cover border border-neutral-200"
                fill={true}
                src={imagePreviews[i] ?? ""}
                unoptimized={true}
              />
              <button
                className="absolute -top-1.5 -right-1.5 size-4 flex items-center justify-center rounded-full bg-neutral-700 text-white hover:bg-neutral-900"
                onClick={() => removeImage(i)}
                type="button"
              >
                <X className="size-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-neutral-100 px-2 py-1.5">
        <div className="flex items-center gap-0.5">
          <input
            accept="image/*"
            className="hidden"
            multiple={true}
            onChange={handleImageSelect}
            ref={fileInputRef}
            type="file"
          />
          <button
            className={cn(
              "size-7 flex items-center justify-center rounded hover:bg-neutral-100 relative",
              images.length > 0 && "text-brand-blue-500",
            )}
            disabled={images.length >= MAX_IMAGES}
            onClick={() => fileInputRef.current?.click()}
            title={
              images.length >= MAX_IMAGES
                ? `En fazla ${MAX_IMAGES} görsel eklenebilir`
                : "Görsel ekle"
            }
            type="button"
          >
            <ImagePlus className="size-4 text-neutral-600" />
            {images.length > 0 && (
              <span className="absolute -top-1 -right-1 size-3.5 flex items-center justify-center rounded-full bg-brand-blue-500 text-white text-[9px] leading-none font-medium">
                {images.length}
              </span>
            )}
          </button>

          <div className="w-px h-4 bg-neutral-200 mx-0.5" />

          <button
            className={cn(
              "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
              editor?.isActive("bulletList") && "bg-neutral-100",
            )}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            type="button"
          >
            <List className="size-4 text-neutral-600" />
          </button>

          <button
            className={cn(
              "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
              editor?.isActive("bold") && "bg-neutral-100",
            )}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            type="button"
          >
            <Bold className="size-4 text-neutral-600" />
          </button>

          <button
            className={cn(
              "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
              editor?.isActive("italic") && "bg-neutral-100",
            )}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            type="button"
          >
            <Italic className="size-4 text-neutral-600" />
          </button>

          <button
            className={cn(
              "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
              editor?.isActive("underline") && "bg-neutral-100",
            )}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            type="button"
          >
            <UnderlineIcon className="size-4 text-neutral-600" />
          </button>

          <button
            className={cn(
              "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
              editor?.isActive("strike") && "bg-neutral-100",
            )}
            onClick={() => editor?.chain().focus().toggleStrike().run()}
            type="button"
          >
            <Strikethrough className="size-4 text-neutral-600" />
          </button>
          <div className="relative" ref={emojiRef}>
            <button
              className="size-7 flex items-center justify-center rounded hover:bg-neutral-100 text-base leading-none"
              onClick={() => setShowEmoji((v) => !v)}
              type="button"
            >
              😊
            </button>
            {showEmoji && (
              <div className="absolute bottom-full right-0 mb-1 z-50 bg-white border border-neutral-200 rounded-lg shadow-lg p-2 grid grid-cols-5 gap-1 w-44">
                {EMOJIS.map((emoji) => (
                  <button
                    className="size-7 flex items-center justify-center rounded hover:bg-neutral-100 text-base"
                    key={emoji}
                    onClick={() => insertEmoji(emoji)}
                    type="button"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleCancel} size="sm" variant="outline">
            İptal
          </Button>
          <Button disabled={isSubmitDisabled} onClick={handleSubmit} size="sm">
            Gönder
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TopicReplyEditor;
